import * as React from 'react'
import { cn } from '../lib/utils'

/**
 * Spec 25 §5.8 — Gedeelde voortgangsbalk voor de onboarding-flow.
 *
 * Wordt gebruikt op:
 *  - backoffice onboarding-lijst (kolom "Voortgang", `size='sm'`)
 *  - backoffice onboarding-detail header (vervangt "X / Y"-tekst, `size='md'`)
 *  - portal kandidaat-dashboard (`/welkom/[token]`, `size='md'`)
 *  - optioneel: medewerker-detail status-regel
 *
 * `value` is een fractie 0..1. `aantalKlaar`/`aantalTotaal` zijn optioneel
 * voor de subtitle "X van Y stappen klaar".
 */
export interface OnboardingVoortgangBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role' | 'aria-valuenow' | 'aria-valuemin' | 'aria-valuemax'> {
  value: number
  aantalKlaar?: number
  aantalTotaal?: number
  label?: string
  size?: 'sm' | 'md'
  /** Toon de "X van Y stappen"-subtitle. Default true wanneer aantal* gegeven zijn. */
  showCount?: boolean
}

export function OnboardingVoortgangBar({
  value,
  aantalKlaar,
  aantalTotaal,
  label,
  size = 'md',
  showCount,
  className,
  ...rest
}: OnboardingVoortgangBarProps) {
  const ratio = Math.max(0, Math.min(1, value))
  const percent = Math.round(ratio * 100)
  const hasCount =
    showCount ?? (typeof aantalKlaar === 'number' && typeof aantalTotaal === 'number')
  const barHeight = size === 'sm' ? 'h-1.5' : 'h-2.5'

  return (
    <div className={cn('w-full', className)} {...rest}>
      {(label || hasCount) && (
        <div className={cn(
          'mb-1 flex items-baseline justify-between',
          size === 'sm' ? 'text-xs' : 'text-sm',
        )}>
          {label ? (
            <span className="font-medium text-[#1A1A18]">{label}</span>
          ) : (
            <span aria-hidden="true" />
          )}
          {hasCount && (
            <span className="text-[#5C5C58]">
              {aantalKlaar} van {aantalTotaal} stappen
            </span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? 'Voortgang'}
        className={cn(
          'w-full overflow-hidden rounded-full bg-[#F0F1EC]',
          barHeight,
        )}
      >
        <div
          className="h-full rounded-full bg-[#CCFA78] transition-[width] duration-300 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}
